import { useState } from "react";
import { Logo } from "@/components/Logo";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, Home, HandHeart, Layers, BarChart3, BellRing, BookOpen, Cake, CalendarClock, CalendarDays, ClipboardList, Clock, GraduationCap, HandCoins, HeartHandshake, Image, LayoutDashboard, LogOut, Mail, Menu, MonitorPlay, ScanLine, Settings, Shield, Sprout, Ticket, Trophy, Users, UsersRound, X, Smartphone, Youtube } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export const ADMIN_NAV = [
  ["Utama", [["/admin", "Dashboard", LayoutDashboard, "dashboard"], ["/admin/members", "Jemaat", Users, "members"], ["/admin/offerings", "Hitung Persembahan", HandCoins, "finance"], ["/admin/event-payments", "Verifikasi Tiket", Ticket, "finance"], ["/admin/checkin", "Check-in QR", ScanLine, "checkin"], ["/admin/reports", "Laporan Kehadiran", BarChart3, "reports"], ["/admin/leaderboard", "Peringkat & Apresiasi", Trophy, "reports"], ["/admin/pastoral", "Pastoral", HeartHandshake, "pastoral"], ["/admin/birthdays", "Ulang Tahun", Cake, "members"], ["/admin/ministry", "My Ministry", HandHeart, "ministry"], ["/admin/internal-meetings", "Pertemuan Internal", CalendarClock, "internal_meetings"]]],
  ["Konten", [["/admin/site", "Konten Beranda", Home, "content"], ["/admin/welcome", "Layar Sapa Jemaat", MonitorPlay, "content"], ["/admin/content/devotions", "Renungan", BookOpen, "content"], ["/admin/content/events", "Events", CalendarDays, "content"], ["/admin/content/sermons", "Sermon", Youtube, "content"], ["/admin/content/schedules", "Jadwal Ibadah", Clock, "content"], ["/admin/content/grow_classes", "Grow Track", Sprout, "content"], ["/admin/content/komsels", "Relationship Community", UsersRound, "content"], ["/admin/communities/report", "Laporan Komunitas", ClipboardList, "reports"], ["/admin/content/community_categories", "Kategori RC & TTS", Layers, "content"], ["/admin/content/gallery", "Galeri", Image, "content"], ["/admin/content/ministries", "Divisi Pelayanan", Layers, "content"], ["/admin/content/serve_questions", "Form Serve-Up", ClipboardList, "content"], ["/admin/elearning", "E-Learning", GraduationCap, "elearning"]]],
  ["Sistem", [["/admin/invitations", "Undangan Pengurus", Mail, "invitations"], ["/admin/roles", "Hak Akses", Shield, "settings"], ["/admin/push", "Notifikasi & PWA", BellRing, "settings"], ["/admin/whatsapp", "WhatsApp Gateway", MessageCircle, "settings"], ["/admin/settings", "Pengaturan", Settings, "settings"]]],
];

export function useAllowed() {
  const { user } = useAuth();
  const perms = user?.perms || [];
  return (area) => !area || perms.includes("*") || perms.includes(area);
}

function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const allowed = useAllowed();
  return (
    <div className="h-full flex flex-col bg-slate-900 text-white p-5">
      <Link to="/" className="flex items-center px-2" data-testid="admin-logo"><Logo dark className="h-7" suffix="OPC" /></Link>
      <nav className="mt-8 flex-1 overflow-y-auto hide-scroll space-y-6">
        {ADMIN_NAV.map(([group, items]) => items.filter(([, , , area]) => allowed(area)).length === 0 ? null : (
          <div key={group}>
            <p className="px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white/40 mb-2">{group}</p>
            {items.filter(([, , , area]) => allowed(area)).map(([to, label, Icon]) => (
              <NavLink key={to} to={to} end={to === "/admin"} onClick={onNavigate} data-testid={`admin-nav-${label.toLowerCase().replace(/\s/g, "-")}`} className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${isActive ? "bg-blue-600 text-white" : "text-white/60 hover:bg-white/5 hover:text-white"}`}>
                <Icon className="w-4 h-4" />{label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="pt-4 border-t border-white/10 space-y-1">
        <Link to="/portal" data-testid="admin-to-portal" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-white/60 hover:bg-white/5 hover:text-white"><Smartphone className="w-4 h-4" />Portal Jemaat</Link>
        <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="admin-logout-button" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-red-300 hover:bg-red-500/10"><LogOut className="w-4 h-4" />Keluar · {user.name.split(" ")[0]}{user.role_label ? ` (${user.role_label})` : ""}</button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const allowed = useAllowed();
  const items = ADMIN_NAV.flatMap(([, list]) => list);
  const match = items.filter(([to]) => (to === "/admin" ? pathname === "/admin" : pathname.startsWith(to)))
    .sort((a, b) => b[0].length - a[0].length)[0];
  const firstAllowed = items.find(([, , , area]) => allowed(area));
  const blocked = match && !allowed(match[3]);
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 lg:grid lg:grid-cols-[16rem_1fr]">
      <aside className="hidden lg:block sticky top-0 h-screen"><Sidebar /></aside>
      <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 py-3">
        <Logo className="h-6" suffix="OPC" />
        <button onClick={() => setOpen(true)} data-testid="admin-mobile-menu" className="w-10 h-10 grid place-items-center rounded-xl hover:bg-slate-100"><Menu className="w-5 h-5" /></button>
      </header>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-72 h-full"><Sidebar onNavigate={() => setOpen(false)} /></div>
          <button className="flex-1 bg-slate-900/50" onClick={() => setOpen(false)} aria-label="Tutup"><X className="w-6 h-6 text-white ml-4" /></button>
        </div>
      )}
      <main key={pathname} className="p-5 md:p-10 max-w-7xl w-full">
        {blocked ? (
          <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm max-w-lg" data-testid="admin-area-blocked">
            <p className="text-lg font-extrabold text-slate-900">Menu ini tidak tersedia untuk role Anda</p>
            <p className="mt-2 text-sm text-slate-500">Hubungi Super Admin bila Anda membutuhkan akses ke menu ini.</p>
            {firstAllowed && <Link to={firstAllowed[0]} data-testid="admin-blocked-redirect" className="mt-5 inline-block text-sm font-bold text-blue-600">← Buka {firstAllowed[1]}</Link>}
          </div>
        ) : <Outlet />}
      </main>
    </div>
  );
}

export function AdminTitle({ eyebrow, title, children }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">{eyebrow}</p>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight text-slate-900 mt-1">{title}</h1>
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}
